import React, { useState } from 'react'

import { FileArchive, Upload as UploadIcon, X as XIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useProcessorSimples } from '../../services/useProcessorSimples'
import { useApuracaoSelecionada } from '../../hooks/seApuracaoSelecionada'

interface ButtonGerarAnaliseProps {
  cnpj?: string
}

export default function ButtonGerarAnlise({ cnpj }: ButtonGerarAnaliseProps) {
  const { apuracaoSelecionada } = useApuracaoSelecionada()
  const [xmlFile, setXmlFile] = useState<File | null>(null)
  const [pgdasFile, setPgdasFile] = useState<File | null>(null)
  const [isAnalysisDialogOpen, setIsAnalysisDialogOpen] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({ xml: 0, pgdas: 0 })
  const [isUploading, setIsUploading] = useState(false)
  const useProcessor = useProcessorSimples()

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Number.parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const validateFile = (file: File) => {
    const maxSize = 1024 * 1024 * 1024 // 1GB in bytes
    if (file.size > maxSize) {
      alert('Arquivo muito grande. O tamanho máximo é 1GB.')
      return false
    }
    if (!file.name.toLowerCase().endsWith('.zip')) {
      alert('Apenas arquivos ZIP são permitidos.')
      return false
    }
    return true
  }

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>, type: 'xml' | 'pgdas') => {
    const file = event.target.files?.[0]
    if (file && validateFile(file)) {
      if (type === 'xml') {
        setXmlFile(file)
      } else {
        setPgdasFile(file)
      }
    }
    // Reset input value to allow selecting the same file again
    event.target.value = ''
  }

  const removeFile = (type: 'xml' | 'pgdas') => {
    if (type === 'xml') {
      setXmlFile(null)
    } else {
      setPgdasFile(null)
    }
  }

  const handleUpload = async () => {
    if (!xmlFile || !pgdasFile) {
      alert('Por favor, selecione ambos os arquivos antes de enviar.')
      return
    }

    setIsUploading(true)
    setUploadProgress({ xml: 0, pgdas: 0 })

    try {
      await useProcessor.mutateAsync({
        xml: xmlFile,
        pgdas: pgdasFile,
        cnpj,
        id_apuracao: apuracaoSelecionada ? apuracaoSelecionada.id : undefined
      })

      setIsAnalysisDialogOpen(false)
      setXmlFile(null)
      setPgdasFile(null)
      setUploadProgress({ xml: 0, pgdas: 0 })
    } catch (error) {
      console.error('Upload failed:', error)
      alert('Erro no upload. Tente novamente.')
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <Dialog open={isAnalysisDialogOpen} onOpenChange={setIsAnalysisDialogOpen}>
      <DialogTrigger asChild>
        <Button className="bg-primary hover:bg-primary-hover text-white">
          <FileArchive className="h-4 w-4 mr-2" />
          Gerar Análise
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Gerar Análise</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/** XML Upload **/}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Arquivo XML (ZIP)</label>
            {xmlFile ? (
              <div className="bg-gray-50 rounded-lg p-3 border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <FileArchive className="h-4 w-4 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 truncate max-w-[220px] overflow-hidden">
                        {xmlFile.name}
                      </p>

                      <p className="text-xs text-gray-500">{formatFileSize(xmlFile.size)}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => removeFile('xml')}
                    disabled={isUploading}
                  >
                    <XIcon className="h-4 w-4" />
                  </Button>
                </div>
                {isUploading && uploadProgress.xml > 0 && (
                  <div className="mt-2">
                    <Progress value={uploadProgress.xml} className="h-2" />
                    <p className="text-xs text-gray-500 mt-1">{uploadProgress.xml}% enviado</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <UploadIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-2">Clique para selecionar ou arraste o arquivo XML</p>
                <p className="text-xs text-gray-500">Apenas arquivos ZIP até 500MB</p>
                <input
                  type="file"
                  accept=".zip"
                  onChange={e => handleFileSelect(e, 'xml')}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={isUploading}
                />
              </div>
            )}
          </div>

          {/** PGDAS Upload **/}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Arquivo PGDAS (ZIP)</label>
            {pgdasFile ? (
              <div className="bg-gray-50 rounded-lg p-3 border">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <FileArchive className="h-4 w-4 text-blue-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 truncate max-w-[200px] overflow-hidden whitespace-nowrap">
                        {pgdasFile.name}
                      </p>

                      <p className="text-xs text-gray-500">{formatFileSize(pgdasFile.size)}</p>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 w-8 p-0"
                    onClick={() => removeFile('pgdas')}
                    disabled={isUploading}
                  >
                    <XIcon className="h-4 w-4" />
                  </Button>
                </div>
                {isUploading && uploadProgress.pgdas > 0 && (
                  <div className="mt-2">
                    <Progress value={uploadProgress.pgdas} className="h-2" />
                    <p className="text-xs text-gray-500 mt-1">{uploadProgress.pgdas}% enviado</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="relative border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
                <UploadIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-600 mb-2">Clique para selecionar ou arraste o arquivo PGDAS</p>
                <p className="text-xs text-gray-500">Apenas arquivos ZIP até 700MB</p>
                <input
                  type="file"
                  accept=".zip"
                  onChange={e => handleFileSelect(e, 'pgdas')}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  disabled={isUploading}
                />
              </div>
            )}
          </div>

          {/** Botões de ação **/}
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsAnalysisDialogOpen(false)} disabled={isUploading}>
              Cancelar
            </Button>
            <Button
              onClick={handleUpload}
              disabled={!xmlFile || !pgdasFile || isUploading}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {isUploading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                  Enviando...
                </>
              ) : (
                <>
                  <UploadIcon className="h-4 w-4 mr-2" />
                  Enviar Arquivos
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
